import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import StatsCard from './components/StatsCard';
import RoomOperationBoard, { type OpRoom, type OpBooking, type OpTask, type OpGuestOp } from './components/RoomOperationBoard';
import CleaningOverview from './components/CleaningOverview';
import OccupancyChart from './components/OccupancyChart';
import Beds24ConnectionTest from './components/Beds24ConnectionTest';
import BusinessSummary from './components/BusinessSummary';
import { RequireAdmin } from '@/components/feature/AuthGuard';
import CasaSectionNav from '@/components/feature/CasaSectionNav';

interface Room {
  id: string;
  name: string;
  room_type: string;
  is_active: boolean;
  mapping_status: string | null;
}

interface Booking {
  id: string;
  room_id: string;
  guest_display_name: string | null;
  arrival_date: string;
  arrival_time: string | null;
  departure_date: string;
  departure_time: string | null;
  adults: number;
  children: number;
  infants: number;
  booking_status: string | null;
  rooms: { name: string } | null;
}

interface HousekeepingTask {
  id: string;
  room_id: string;
  status: string;
  notes: string | null;
  created_at: string;
  rooms: { name: string } | null;
  assigned_staff: { id: string; full_name: string } | null;
}

interface StaffOnDuty {
  id: string;
  full_name: string;
  status: string;
  currentTask: string;
}

interface BookingWindowData { id: string; room_id: string; arrival_date: string; departure_date: string }

// ── JST date helpers ──

function getTodayJST(): string {
  const fmt = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = fmt.formatToParts(new Date());
  const y = parts.find((p) => p.type === 'year')?.value ?? '';
  const m = parts.find((p) => p.type === 'month')?.value ?? '';
  const d = parts.find((p) => p.type === 'day')?.value ?? '';
  return `${y}-${m}-${d}`;
}

function addDaysJST(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  date.setUTCDate(date.getUTCDate() + days);
  const yy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function formatTodayFullJST(): string {
  const fmt = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'short',
  });
  return fmt.format(new Date());
}

// ── Component ──

export default function Dashboard() {
  const { user, profile } = useAuth();

  // Staff display name from profiles.full_name
  const staffName = profile?.full_name || null;

  // ── Today stats (always Asia/Tokyo today) ──
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [arrivals, setArrivals] = useState<Booking[]>([]);
  const [departures, setDepartures] = useState<Booking[]>([]);
  const [tasks, setTasks] = useState<HousekeepingTask[]>([]);
  const [staffOnDuty, setStaffOnDuty] = useState<StaffOnDuty[]>([]);
  const [sevenDayBookings, setSevenDayBookings] = useState<BookingWindowData[]>([]);
  const [occupiedToday, setOccupiedToday] = useState(0);

  // ── RoomOperationBoard: date-aware ──
  const [selectedDate, setSelectedDate] = useState<string>(getTodayJST());
  const [opBookings, setOpBookings] = useState<OpBooking[]>([]);
  const [opTasks, setOpTasks] = useState<OpTask[]>([]);
  const [opGuestOps, setOpGuestOps] = useState<OpGuestOp[]>([]);
  const [opLoading, setOpLoading] = useState(true);
  const [opError, setOpError] = useState<string | null>(null);
  const [guestOpsWarning, setGuestOpsWarning] = useState<string | null>(null);
  const [opActionLoading, setOpActionLoading] = useState(false);
  const [opActionMessage, setOpActionMessage] = useState<string | null>(null);
  const [opActionIsError, setOpActionIsError] = useState(false);

  // ── Room status data (for 部屋状態 computation) ──
  const [roomStatusBookings, setRoomStatusBookings] = useState<OpBooking[]>([]);
  const [roomStatusTasks, setRoomStatusTasks] = useState<OpTask[]>([]);

  const todayStr = getTodayJST();
  const todayFullDisplay = formatTodayFullJST();

  // ── Fetch today's core data ──
  const fetchTodayData = useCallback(async () => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    try {
      const [
        roomsResult, arrivalsResult, departuresResult, tasksResult,
        occupancyTodayResult,
      ] = await Promise.all([
        supabase.from('rooms').select('id, name, room_type, is_active, mapping_status').eq('is_active', true),
        supabase.from('bookings')
          .select('id, room_id, guest_display_name, arrival_date, arrival_time, departure_date, departure_time, adults, children, infants, booking_status, rooms!inner(name)')
          .eq('arrival_date', todayStr),
        supabase.from('bookings')
          .select('id, room_id, guest_display_name, arrival_date, arrival_time, departure_date, departure_time, adults, children, infants, booking_status, rooms!inner(name)')
          .eq('departure_date', todayStr),
        supabase.from('housekeeping_tasks')
          .select('id, room_id, status, notes, created_at, rooms!inner(name)')
          .eq('task_date', todayStr),
        supabase.from('bookings')
          .select('id')
          .lte('arrival_date', todayStr)
          .gt('departure_date', todayStr)
          .not('booking_status', 'eq', 'cancelled')
          .not('operational_status', 'eq', 'no_show'),
      ]);

      if (cancelled) return;

      if (roomsResult.error) throw new Error(`部屋情報の取得に失敗: ${roomsResult.error.message}`);
      if (arrivalsResult.error) throw new Error(`チェックイン予約の取得に失敗: ${arrivalsResult.error.message}`);
      if (departuresResult.error) throw new Error(`チェックアウト予約の取得に失敗: ${departuresResult.error.message}`);
      if (tasksResult.error) throw new Error(`清掃タスクの取得に失敗: ${tasksResult.error.message}`);
      if (occupancyTodayResult.error) throw new Error(`稼働率の取得に失敗: ${occupancyTodayResult.error.message}`);

      const fetchedRooms = (roomsResult.data ?? []) as Room[];
      const fetchedArrivals = (arrivalsResult.data ?? []) as unknown as Booking[];
      const fetchedDepartures = (departuresResult.data ?? []) as unknown as Booking[];
      const fetchedTasks = (tasksResult.data ?? []) as unknown as HousekeepingTask[];
      const occupancyTodayData = (occupancyTodayResult.data ?? []) as { id: string }[];

      const activeRoomCount = fetchedRooms.length;
      const occupiedCount = occupancyTodayData.length;

      // Task assignments
      let tasksWithStaff: HousekeepingTask[] = fetchedTasks;
      if (fetchedTasks.length > 0) {
        const taskIds = fetchedTasks.map((t) => t.id);
        const { data: assignments } = await supabase.from('task_assignments').select('task_id, staff_id').in('task_id', taskIds).is('unassigned_at', null);
        if (assignments && assignments.length > 0) {
          const staffIds = [...new Set(assignments.map((a) => a.staff_id))];
          const { data: staffProfiles } = await supabase.from('profiles').select('id, full_name').in('id', staffIds);
          const staffMap = new Map<string, string>();
          if (staffProfiles) staffProfiles.forEach((p) => staffMap.set(p.id, p.full_name));
          const assignMap = new Map<string, string>();
          assignments.forEach((a) => { const name = staffMap.get(a.staff_id); if (name) assignMap.set(a.task_id, name); });
          tasksWithStaff = fetchedTasks.map((t) => ({ ...t, assigned_staff: assignMap.has(t.id) ? { id: '', full_name: assignMap.get(t.id)! } : null }));
        }
      }

      // Staff on duty
      const { data: activeProfiles } = await supabase.from('profiles').select('id, full_name').eq('is_active', true);
      let dutyStaff: StaffOnDuty[] = [];
      if (activeProfiles && activeProfiles.length > 0) {
        const timesheetResults = await Promise.all(activeProfiles.map((p) => supabase.rpc('compute_daily_timesheet', { p_staff_id: p.id, p_from_date: todayStr, p_to_date: todayStr })));
        timesheetResults.forEach((result, idx) => {
          if (result.error || !result.data || result.data.length === 0) return;
          const record = result.data[0];
          if (record.status === 'working' || record.status === 'on_break') {
            dutyStaff.push({ id: activeProfiles[idx].id, full_name: activeProfiles[idx].full_name ?? record.full_name ?? 'Unknown', status: record.status, currentTask: record.status === 'on_break' ? '休憩中' : '勤務中' });
          }
        });
      }

      if (cancelled) return;

      setRooms(fetchedRooms);
      setArrivals(fetchedArrivals);
      setDepartures(fetchedDepartures);
      setTasks(tasksWithStaff);
      setStaffOnDuty(dutyStaff);
      setOccupiedToday(occupiedCount);
    } catch (err: unknown) {
      if (cancelled) return;
      if (err instanceof Error) setError(err.message);
      else setError('データの取得中に予期せぬエラーが発生しました');
    } finally {
      if (!cancelled) setLoading(false);
    }
    return () => { cancelled = true; };
  }, [todayStr]);

  const fetchSevenDayOccupancy = useCallback(async (startDate: string) => {
    const endDate = addDaysJST(startDate, 14);
    const { data, error: rangeError } = await supabase.from('bookings')
      .select('id, room_id, arrival_date, departure_date')
      .lt('arrival_date', endDate)
      .gt('departure_date', startDate)
      .not('booking_status', 'eq', 'cancelled')
      .not('operational_status', 'eq', 'no_show');
    if (rangeError) return;

    setSevenDayBookings((data ?? []) as BookingWindowData[]);
  }, []);

  // ── Fetch RoomOperationBoard data (selectedDate-aware) ──
  const fetchOpData = useCallback(async (dateStr: string) => {
    let cancelled = false;
    setOpLoading(true);
    setOpError(null);
    setGuestOpsWarning(null);

    try {
      // bookings: OUT + IN + STAYOVER for selectedDate
      const [opBookingsResult, opTasksResult] = await Promise.all([
        supabase.from('bookings')
          .select('id, room_id, guest_display_name, arrival_date, arrival_time, departure_date, departure_time, booking_status, operational_status, booking_channel, source_override, adults, children, infants, beds24_booking_id, beds24_status, actual_check_in_at, actual_check_out_at, checked_in_by, checked_out_by, booking_origin')
          .or(`arrival_date.eq.${dateStr},departure_date.eq.${dateStr},and(arrival_date.lt.${dateStr},departure_date.gt.${dateStr})`)
          .not('booking_status', 'eq', 'cancelled'),
        supabase.from('housekeeping_tasks')
          .select('id, room_id, status, task_date')
          .eq('task_date', dateStr),
      ]);

      if (cancelled) return;

      let opBookingsErr: string | null = null;
      let opTasksErr: string | null = null;
      if (opBookingsResult.error) opBookingsErr = opBookingsResult.error.message;
      if (opTasksResult.error) opTasksErr = opTasksResult.error.message;

      const opErrorMsg = [opBookingsErr, opTasksErr].filter(Boolean).join('; ');
      if (opErrorMsg) {
        setOpError(opErrorMsg);
        setOpLoading(false);
        return;
      }

      const fetchedOpBookings: OpBooking[] = (opBookingsResult.data ?? []) as OpBooking[];
      const fetchedOpTasks: OpTask[] = (opTasksResult.data ?? []) as OpTask[];

      // guest_operations
      let opsWarning: string | null = null;
      let opsData: OpGuestOp[] = [];
      const allBookingIds = fetchedOpBookings.map((b) => b.id);
      if (allBookingIds.length > 0) {
        try {
          const { data: goData, error: goError } = await supabase
            .from('guest_operations')
            .select('id, booking_id, early_checkin_requested, early_checkin_approved, late_checkout_requested, late_checkout_approved')
            .in('booking_id', allBookingIds);
          if (goError) {
            opsWarning = `ゲストオペレーションの取得に失敗: ${goError.message}`;
          } else {
            opsData = (goData ?? []) as OpGuestOp[];
          }
        } catch {
          opsWarning = 'ゲストオペレーションの取得中に予期せぬエラーが発生しました';
        }
      }

      if (cancelled) return;
      setOpBookings(fetchedOpBookings);
      setOpTasks(fetchedOpTasks);
      setOpGuestOps(opsData);
      setGuestOpsWarning(opsWarning);
      setOpLoading(false);
      setOpError(null);

      // ── Fetch room status data (all checked_in / checked_out bookings + incomplete tasks) ──
      try {
        const [rsBookingsResult, rsTasksResult] = await Promise.all([
          supabase.from('bookings')
            .select('id, room_id, guest_display_name, arrival_date, arrival_time, departure_date, departure_time, booking_status, operational_status, booking_channel, source_override, adults, children, infants, beds24_booking_id, beds24_status, actual_check_in_at, actual_check_out_at, checked_in_by, checked_out_by, booking_origin')
            .or('operational_status.eq.checked_in,operational_status.eq.checked_out,and(operational_status.eq.expected,beds24_booking_id.not.is.null)')
            .not('booking_status', 'eq', 'cancelled'),
          supabase.from('housekeeping_tasks')
            .select('id, room_id, status, task_date, departing_booking_id'),
        ]);
        if (!cancelled) {
          if (!rsBookingsResult.error) setRoomStatusBookings((rsBookingsResult.data ?? []) as OpBooking[]);
          if (!rsTasksResult.error) setRoomStatusTasks((rsTasksResult.data ?? []) as OpTask[]);
        }
      } catch {
        // room status fetch is non-critical — leave defaults
      }
    } catch (err: unknown) {
      if (cancelled) return;
      setOpLoading(false);
      if (err instanceof Error) setOpError(err.message);
      else setOpError('運用ボードデータの取得に失敗しました');
    }
    return () => { cancelled = true; };
  }, []);

  const handleCheckIn = useCallback(async (bookingId: string) => {
    setOpActionLoading(true);
    setOpActionMessage(null);
    setOpActionIsError(false);
    try {
      const { data, error } = await supabase.rpc('check_in_booking', { p_booking_id: bookingId });
      if (error) {
        setOpActionIsError(true);
        setOpActionMessage(error.message || 'チェックインに失敗しました');
        return;
      }
      const result = data as Record<string, unknown> | null;
      if (result?.success) {
        setOpActionIsError(false);
        setOpActionMessage('チェックインを記録しました。');
        await fetchOpData(selectedDate);
      } else {
        setOpActionIsError(true);
        const err = (result?.error as string) || '';
        if (err === 'room_occupied') {
          setOpActionMessage('この部屋には現在滞在中のゲストがいます。');
        } else if (err === 'room_not_ready') {
          const reason = (result?.reason as string) || '';
          if (reason === 'housekeeping_incomplete') {
            setOpActionMessage('この部屋は清掃・準備が完了していません。');
          } else if (reason === 'housekeeping_task_missing') {
            setOpActionMessage('清掃タスクを確認してください。');
          } else {
            setOpActionMessage('この部屋はチェックイン可能な状態ではありません。');
          }
        } else {
          setOpActionMessage((result?.error as string) || 'チェックインに失敗しました');
        }
      }
    } catch {
      setOpActionIsError(true);
      setOpActionMessage('通信エラーが発生しました。');
    } finally {
      setOpActionLoading(false);
    }
  }, [selectedDate, fetchOpData]);

  const handleCheckOut = useCallback(async (bookingId: string) => {
    setOpActionLoading(true);
    setOpActionMessage(null);
    setOpActionIsError(false);
    try {
      const { data, error } = await supabase.rpc('check_out_booking', { p_booking_id: bookingId });
      if (error) {
        setOpActionIsError(true);
        setOpActionMessage(error.message || 'チェックアウトに失敗しました');
        return;
      }
      setOpActionIsError(false);
      const result = data as Record<string, unknown> | null;
      const taskCreated = result?.task_created;
      const msg = taskCreated
        ? 'チェックアウトを記録し、清掃タスクを作成しました。'
        : 'チェックアウトを記録しました。';
      setOpActionMessage(msg);
      await fetchOpData(selectedDate);
    } catch {
      setOpActionIsError(true);
      setOpActionMessage('通信エラーが発生しました。');
    } finally {
      setOpActionLoading(false);
    }
  }, [selectedDate, fetchOpData]);

  // 移行時在室予約のチェックアウト: check_in → check_out の2段階
  const handleImportedCheckOut = useCallback(async (bookingId: string) => {
    setOpActionLoading(true);
    setOpActionMessage(null);
    setOpActionIsError(false);
    try {
      // Step 1: check_in_booking（補完）
      const { error: ciError } = await supabase.rpc('check_in_booking', { p_booking_id: bookingId });
      if (ciError) {
        setOpActionIsError(true);
        setOpActionMessage(ciError.message || 'チェックイン記録の補完に失敗しました');
        return;
      }
      // Step 2: check_out_booking
      const { data: coData, error: coError } = await supabase.rpc('check_out_booking', { p_booking_id: bookingId });
      if (coError) {
        // check_in succeeded but check_out failed — refresh to show 在室
        setOpActionIsError(true);
        setOpActionMessage(`チェックインを記録しましたが、チェックアウトに失敗しました: ${coError.message || '不明なエラー'}`);
        await fetchOpData(selectedDate);
        return;
      }
      const result = coData as Record<string, unknown> | null;
      const taskCreated = result?.task_created;
      const msg = taskCreated
        ? 'チェックイン・チェックアウトを記録し、清掃タスクを作成しました。'
        : 'チェックイン・チェックアウトを記録しました。';
      setOpActionIsError(false);
      setOpActionMessage(msg);
      await fetchOpData(selectedDate);
    } catch {
      setOpActionIsError(true);
      setOpActionMessage('通信エラーが発生しました。');
    } finally {
      setOpActionLoading(false);
    }
  }, [selectedDate, fetchOpData]);

  // ── No-show ──
  const handleNoShow = async (bookingId: string, reason: string) => {
    setOpActionLoading(true);
    setOpActionMessage(null);
    setOpActionIsError(false);
    try {
      const { error } = await supabase.rpc('mark_booking_no_show', {
        p_booking_id: bookingId,
        p_reason: reason,
      });
      if (error) {
        setOpActionIsError(true);
        setOpActionMessage('ノーショーを記録できませんでした');
        return;
      }
      setOpActionIsError(false);
      setOpActionMessage('ノーショーを記録しました');
      await fetchTodayData();
      await fetchOpData(selectedDate);
    } catch {
      setOpActionIsError(true);
      setOpActionMessage('ノーショーを記録できませんでした');
    } finally {
      setOpActionLoading(false);
    }
  };

  // ── Effects ──

  useEffect(() => {
    const cleanup = fetchTodayData();
    return () => { cleanup?.then((c) => c?.()); };
  }, [fetchTodayData]);

  useEffect(() => {
    const cleanup = fetchOpData(selectedDate);
    return () => { cleanup?.then((c) => c?.()); };
  }, [selectedDate, fetchOpData]);

  useEffect(() => {
    void fetchSevenDayOccupancy(selectedDate);
  }, [selectedDate, fetchSevenDayOccupancy]);

  // ── Computed ──

  const activeRoomCount = rooms.length;
  const mappedRoomCount = rooms.filter((r) => r.mapping_status === 'approved').length;
  const allRoomsMapped = activeRoomCount > 0 && mappedRoomCount === activeRoomCount;
  const checkIns = arrivals.length;
  const checkOuts = departures.length;
  const occupancyRate = activeRoomCount > 0 ? Math.round((occupiedToday / activeRoomCount) * 100) : 0;

  const notRequiredTasks = tasks.filter((t) => t.status === 'not_required').length;
  const completedOrReadyTasks = tasks.filter((t) => t.status === 'completed' || t.status === 'ready').length;
  const cleaningDenominator = tasks.length - notRequiredTasks;
  const cleaningProgress = cleaningDenominator > 0 ? Math.round((completedOrReadyTasks / cleaningDenominator) * 100) : 0;

  return (
    <div className="max-w-7xl">
      <CasaSectionNav />
      {/* ── Page Header ── */}
      <div className="mb-3 pl-4 md:pl-6 lg:pl-8 min-[1440px]:pl-10 pr-4 md:pr-6 lg:pr-8 min-[1440px]:pr-10">
        <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-4">
          <h2 className="text-lg md:text-2xl font-bold font-heading tracking-tight" style={{ color: '#252824' }}>
            {todayFullDisplay}
          </h2>
          <p className="text-sm md:text-base sm:text-right" style={{ color: '#6B6B67' }}>
            お疲れ様です、{staffName || 'スタッフ'}さん
          </p>
        </div>
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <span className="w-7 h-7 flex items-center justify-center">
            <i className="ri-loader-4-line text-2xl animate-spin" style={{ color: '#234638' }}></i>
          </span>
          <p className="text-sm" style={{ color: '#6B6B67' }}>データを読み込み中...</p>
        </div>
      )}

      {/* ── Error ── */}
      {!loading && error && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F0EDE5' }}>
            <i className="ri-error-warning-line text-xl" style={{ color: '#A33A32' }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: '#A33A32' }}>データの取得に失敗しました</p>
          <p className="text-xs max-w-md text-center" style={{ color: '#6B6B67' }}>{error}</p>
          <button onClick={() => { fetchTodayData(); }} className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap" style={{ backgroundColor: '#234638', color: '#FFFEFA' }}>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line text-sm"></i></span>
            再試行
          </button>
        </div>
      )}

      {/* ── Data ── */}
      {!loading && !error && (
        <>
          {/* Compact daily snapshot + selected-date seven-day booking window */}
          <div className="grid grid-cols-1 lg:grid-cols-[380px_minmax(0,1fr)] gap-3 mb-4 items-start">
            <div className="space-y-2 self-start">
              <div className="grid grid-cols-2 gap-2">
                <StatsCard label="本日のチェックイン" value={checkIns} subLabel={`${checkIns}組到着予定`} icon="ri-login-box-line" iconColor="#A8843A" />
                <StatsCard label="本日のチェックアウト" value={checkOuts} subLabel={`${checkOuts}組退室予定`} icon="ri-logout-box-line" iconColor="#234638" />
                <StatsCard label="稼働率" value={allRoomsMapped ? `${occupancyRate}%` : '--'} subLabel={allRoomsMapped ? `${occupiedToday}/${activeRoomCount}室 稼働` : `Beds24予約未同期 · ${mappedRoomCount}/${activeRoomCount}`} icon="ri-hotel-line" iconColor="#234638" />
                <StatsCard label="清掃進捗" value={cleaningDenominator > 0 ? `${cleaningProgress}%` : '0件'} subLabel={cleaningDenominator > 0 ? `${completedOrReadyTasks}/${cleaningDenominator}室 完了` : '本日の清掃タスクはありません'} icon="ri-brush-3-line" iconColor="#A8843A" />
              </div>
              <div className="rounded-md border px-3 py-2 flex items-center gap-2 min-h-[38px]" style={{ backgroundColor: '#FFFEFA', borderColor: '#DED9CC' }}>
                <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F0EDE5', color: '#234638' }}><i className="ri-team-line text-xs" /></span>
                <span className="text-[10px] font-semibold whitespace-nowrap" style={{ color: '#252824' }}>本日のスタッフ配置</span>
                <span className="text-[10px] whitespace-nowrap" style={{ color: '#6B6B67' }}>{staffOnDuty.length}名</span>
                <span className="text-[9px] truncate ml-auto" style={{ color: staffOnDuty.length ? '#234638' : '#A8843A' }}>{staffOnDuty.length ? staffOnDuty.map((staff) => staff.full_name).join('、') : '未配置'}</span>
              </div>
            </div>
            <OccupancyChart
              rooms={rooms}
              bookings={sevenDayBookings}
              dates={Array.from({ length: 14 }, (_, index) => addDaysJST(selectedDate, index))}
              onPrevious={() => setSelectedDate((date) => addDaysJST(date, -14))}
              onNext={() => setSelectedDate((date) => addDaysJST(date, 14))}
            />
          </div>

          {/* Main grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left column */}
            <div className="lg:col-span-2 space-y-4">
              <RoomOperationBoard
                rooms={rooms as OpRoom[]}
                bookings={opBookings}
                tasks={opTasks}
                guestOps={opGuestOps}
                loading={loading || opLoading}
                error={error || opError}
                onRetry={() => fetchOpData(selectedDate)}
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                onCheckIn={handleCheckIn}
                onCheckOut={handleCheckOut}
                onImportedCheckOut={handleImportedCheckOut}
                onNoShow={handleNoShow}
                actionLoading={opActionLoading}
                actionMessage={opActionMessage}
                actionIsError={opActionIsError}
                onClearMessage={() => setOpActionMessage(null)}
                roomStatusBookings={roomStatusBookings}
                roomStatusTasks={roomStatusTasks}
              />
              {guestOpsWarning && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-md text-xs" style={{ backgroundColor: '#F6F4EE', color: '#A8843A', border: '1px solid #DED9CC' }}>
                  <span className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-information-line text-sm"></i></span>
                  {guestOpsWarning}
                </div>
              )}
              <CleaningOverview tasks={tasks} />
            </div>

            {/* Right column */}
            <div className="space-y-4">
              {profile?.access_level === 'super_admin' && (
                <BusinessSummary casa={{ checkIns, checkOuts, cleaningPending: Math.max(cleaningDenominator - completedOrReadyTasks, 0) }} />
              )}

              {/* Quick Actions — compact */}
              <div className="rounded-md border overflow-hidden" style={{ backgroundColor: '#FFFEFA', borderColor: '#DED9CC' }}>
                <div className="px-4 py-3" style={{ borderBottom: '1px solid #DED9CC' }}>
                  <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#6B6B67' }}>クイックアクション</h3>
                </div>
                <div className="p-3 space-y-1.5">
                  <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap" style={{ backgroundColor: '#234638', color: '#FFFEFA' }}>
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-circle-line text-sm"></i></span>
                    清掃タスクを追加
                  </button>
                  <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap" style={{ backgroundColor: '#F0EDE5', color: '#252824' }}>
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-add-line text-sm"></i></span>
                    予約を手動登録
                  </button>
                  <button className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap" style={{ backgroundColor: '#F0EDE5', color: '#252824' }}>
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-megaphone-line text-sm"></i></span>
                    スタッフ全体通知
                  </button>
                </div>
              </div>

              <RequireAdmin>
                <Beds24ConnectionTest />
              </RequireAdmin>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
